// src/routes/campioni/index.tsx
import React, { useState } from 'react';
import {
  Container,
  Title,
  Paper,
  Group,
  Button,
  Stack,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { RequestModal } from '../requests/components/RequestModal';
import { RequestsTableServerPaginated } from '../requests/components/RequestsTableServerPaginated';
import { useRequestTypePageState } from '../../hooks/use-request-type-page-state';
import {
  RequestTypeFilters,
  RequestStatusCountsBadges,
} from '../../components/requests/filters';
import { Request as RequestType, RequestType as RequestTypeEnum, RequestStatus } from '../../types/api';
import { getStatusesForType } from '../../utils/request-status-utils';

// Ordine chip: gli stati di Pre-Campionatura per primi (in coda dal team Ufficio Stile),
// poi il flusso Campione vero e proprio.
const CAMPIONI_VISIBLE_STATUSES = [
  RequestStatus.PreCampionatura,
  RequestStatus.PreCampionaturaCompleted,
  RequestStatus.New,
  RequestStatus.InProgress,
  RequestStatus.PendingCompletion,
  RequestStatus.Suspended,
  RequestStatus.Completed,
  RequestStatus.Cancelled,
];

export function CampioniPage() {
  const navigate = useNavigate();
  const [createModalOpened, setCreateModalOpened] = useState(false);

  const state = useRequestTypePageState({
    type: RequestTypeEnum.Campionatura,
    route: '/campioni',
  });

  const handleViewRequest = (id: number | string) => {
    navigate({ to: `/requests/${id}`, search: { from: '/campioni' } });
  };

  const handleEditRequest = (id: number | string) => {
    navigate({ to: `/requests/${id}/edit`, search: { from: '/campioni' } });
  };

  const handleRequestCreated = (_request: RequestType) => {
    setCreateModalOpened(false);
    state.invalidate();
  };

  return (
    <Container size="xl" py="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Campionatura</Title>
          <Button
            color="blue"
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateModalOpened(true)}
            styles={{ label: { color: 'white' }, section: { color: 'white' } }}
          >
            Nuova Richiesta Campionatura
          </Button>
        </Group>

        <Paper withBorder p="md">
          <Stack gap="md">
            <RequestStatusCountsBadges
              type={RequestTypeEnum.Campionatura}
              aggregates={state.aggregates}
              loading={state.aggregatesLoading}
              activeStatus={state.activeStatus}
              onStatusToggle={state.handleStatusToggle}
              visibleStatuses={CAMPIONI_VISIBLE_STATUSES}
            />

            <RequestTypeFilters
              value={state.filters}
              onChange={state.handleFiltersChange}
              onReset={state.handleResetFilters}
              availableYears={state.availableYears}
              availableStatuses={getStatusesForType(RequestTypeEnum.Campionatura)}
            />

            <RequestsTableServerPaginated
              data={state.items}
              onView={handleViewRequest}
              onEdit={handleEditRequest}
              loading={state.isLoading}
              fetching={state.isFetching}
              totalCount={state.totalCount}
              totalPages={state.totalPages}
              pageNumber={state.pageNumber}
              pageSize={state.pageSize}
              onPageChange={state.handlePageChange}
              onPageSizeChange={state.handlePageSizeChange}
            />
          </Stack>
        </Paper>
      </Stack>

      <RequestModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        mode="create"
        onSuccess={handleRequestCreated}
        defaultRequestType={String(RequestTypeEnum.Campionatura)}
      />
    </Container>
  );
}
